import axios, {AxiosError} from 'axios';

import type {ProductData, ManagedShippingDataProps} from '@/redux/reducers/productReducer';
import type {
  BasketedGroupProps,
  BasketProps,
  CurrencyType,
  ShippingProps,
} from '@/redux/reducers/typings';
import type {BasketedItemProps} from '@/molecules/Cards/Basketed/typings';
import type {
  ArtaSupportedCurrency,
  ArtaQuoteRequest,
  ArtaHostedSession,
  ArtaShippingEstimate,
  ArtaLocation,
  ArtaObject,
  ArtaQuoteType,
  ArtworkProvider,
  SessionsMap,
  EstimatesMap,
  AsyncState,
  ArtaInsurance,
} from './typings';

const ARTA_BASE_URL = 'https://api.arta.io';
const ARTA_PREFIX = 'ARTA_APIKey';
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;
export const STORAGE_KEY_ESTIMATES = 'artaShippingEstimatesMap';
export const STORAGE_KEY_SESSIONS = 'artaHostedSessionsMap';
export const ARTA_INSURANCE: ArtaInsurance = 'arta_transit_insurance';
export const MANAGED_SHIPPING_COST_UNAVAILABLE = 'MANAGED_SHIPPING_COST_UNAVAILABLE';
export const MANAGED_SHIPPING_ESTIMATE_FAILED = 'MANAGED_SHIPPING_ESTIMATE_FAILED';
export const AF_CONTACT_SUPPORT_URL =
  'https://help.artfinder.com/article/765-how-to-contact-artfinder-customer-care';

/**
 * @description Utility functions for handling Unix timestamps.
 */
export const unixTimestamp = {
  /**
   * @description Converts a Date object to a Unix timestamp.
   * @param {Date|undefined} date - Date object to convert to Unix timestamp.
   * If not provided, current date is used.
   * @returns Unix timestamp in seconds.
   */
  get: (date?: Date) => Math.floor((date ? date.getTime() : Date.now()) / 1000),
  /**
   * @description Checks if a given Unix timestamp has expired based on the specified expiry duration.
   * @param timestamp - Unix timestamp in seconds or any timestamp in milliseconds.
   * @param expiry - Expiry duration in milliseconds (default is 7 days).
   * @returns `true` if the timestamp has expired, `false` otherwise.
   */
  isExpired: (timestamp: number, expiry = SEVEN_DAYS_MS): boolean => {
    const now = Date.now();
    /**
     * check if unix timestamp or not,
     * - convert to milliseconds if needed
     */
    const timestampMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return now - timestampMs > expiry;
  },
};

/**
 * @description Formats a value to a float number.
 * @param {unknown} value - The value to be formatted.
 * @returns {number | null} - The formatted float number or null if formatting fails.
 */
export const formatToFloat = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }
  if (typeof value === 'number' && isNaN(value)) {
    return null;
  }
  if (typeof value === 'number') return value;

  const parsed = parseFloat(value.replace(/,/g, ''));
  return isNaN(parsed) ? null : parsed;
};

/**
 * @description Checks if an object is empty or null/undefined.
 * @param {object | null | undefined} value - The object to check.
 * @returns {boolean} - `true` if the object is empty or null/undefined, `false` otherwise.
 */
export const isObjectEmpty = (value?: Record<string, any> | null) => {
  if (!value) return true;
  return Object.keys(value).length === 0;
};

/**
 * @description Formats a numeric value to a price string with two decimal places.
 * Examples: 10 -> "10.00", 10.5 -> "10.50", "20.456" -> "20.46", 1234.5678 -> "1,234.57"
 * @param {string | number} value - The numeric value to be formatted.
 * @returns {string} - The formatted price string.
 */
export const formatNumericToPrice = (value: number | string): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(typeof value === 'string' ? parseFloat(value) : value);

/**
 * @description Extracts a meaningful error message from various error types.
 * @param {object} param0 - The parameters object.
 * @param {unknown} param0.error - The error to extract the message from.
 * @param {string} param0.defaultMessage - The default message to return if no specific message is found.
 * @return {string} - The extracted error message or the default message.
 */
export const getArtaErrorMessage = ({
  error,
  defaultMessage,
}: {
  error: unknown;
  defaultMessage: string;
}) => {
  if (error instanceof AxiosError) {
    return error.message;
  } else if (error instanceof Error) {
    return error.message;
  }
  /** IF ARTA ERROR */
  if (
    typeof error === 'object' &&
    !!error &&
    'errors' in error &&
    typeof error.errors === 'object' &&
    !!error.errors
  ) {
    return Object.values(error.errors).flat().join(', ');
  }
  return defaultMessage;
};

/**
 * @description Extracts the necessary parameters from a hosted session object.
 * @param session - The hosted session object.
 * @returns An object containing the id and token of the hosted session.
 */
export const getHostedSessionParams = (session: ArtaHostedSession) => {
  return {
    id: session.id,
    token: session.private_token,
  };
};

/**
 * @description Extracts a summary of the location details.
 * @param {object} artaLocation - The Arta location large object.
 * @returns {object} A summary object with the brief location details.
 */
const getLocationSummary = ({
  country,
  region,
  city,
  postal_code,
}: ArtaLocation): Pick<ArtaLocation, 'country' | 'region' | 'city' | 'postal_code'> => ({
  country,
  region,
  city,
  postal_code,
});

export const getPricesRange = (prices: number[]): ArtaShippingEstimate['shipping_price'] => {
  if (prices.length === 0) {
    return {min: null, max: null};
  }
  if (prices.length === 1) {
    return {min: prices[0], max: null};
  }

  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  if (minPrice === maxPrice) {
    return {min: minPrice, max: null};
  }
  return {min: minPrice, max: maxPrice};
};

export const getRelevantDisqualifications = ({
  requestedQuoteTypes,
  disqualifications,
}: {
  requestedQuoteTypes?: ArtaQuoteType[];
  disqualifications?: ArtaQuoteRequest['disqualifications'];
}): {
  type: ArtaQuoteType;
  message: string;
}[] => {
  if (!disqualifications || disqualifications.length === 0) {
    return [];
  }
  const quoteTypes: ArtaQuoteType[] = requestedQuoteTypes?.length
    ? requestedQuoteTypes
    : ['parcel', 'premium', 'select', 'self_ship'];
  const requestedSet = new Set(quoteTypes);
  /** map quote type -> collected reasons */
  const map = new Map<ArtaQuoteType, string[]>();

  disqualifications
    .flatMap((current) =>
      current.quote_types.map((quoteType) => ({quoteType, reason: current.reason})),
    )
    .forEach(({quoteType, reason}) => {
      if (requestedSet.has(quoteType)) {
        if (!map.has(quoteType)) map.set(quoteType, []);
        map.get(quoteType)!.push(reason);
      }
    });

  /** build output array */
  return quoteTypes
    .filter((quoteType) => map.has(quoteType))
    .map((quoteType) => ({
      type: quoteType,
      message: map.get(quoteType)?.join('. ') as string,
    }));
};

/**
 * @description Extracts the shipping estimate from the Arta quote large quote request object.
 * @param {object} artaQuoteRequest - The Arta quote request object.
 * @param {string} productSlug - The product slug.
 * @param {ArtaQuoteType[]} quoteTypes - (optional) The list of requested quote types ( e.g ( ['parcel', 'premium', 'self_ship'] )).
 * @returns {object} The shipping estimate object to fit our needs.
 */
export const getShippingEstimate = (
  artaQuoteRequest: ArtaQuoteRequest,
  productSlug: string,
  quoteTypes?: ArtaQuoteType[],
): ArtaShippingEstimate => {
  const from = getLocationSummary(artaQuoteRequest.origin);
  const to = getLocationSummary(artaQuoteRequest.destination);
  const quotesPrices = artaQuoteRequest.quotes.map((quote) =>
    typeof quote.total === 'string' ? parseFloat(quote.total) : quote.total,
  );

  return {
    from,
    to,
    timestamp: unixTimestamp.get(),
    product_slug: productSlug,
    shipping_price: getPricesRange(quotesPrices),
    currency: artaQuoteRequest.currency,
    status: artaQuoteRequest.status,
    disqualifications: getRelevantDisqualifications({
      requestedQuoteTypes: quoteTypes,
      disqualifications: artaQuoteRequest.disqualifications,
    }),
  };
};

/**
 * @description Determines the subtype of the Arta object based on the product category and type.
 * @param {string} categorySlug product category slug (e.g., "painting-oil", "sculpture", "photography", etc.)
 * @param {string|undefined} type (optional)product type (e.g., "framed", "unframed", etc.)
 * @returns {string} The `ArtaObjectSubType` determined subtype string of the Arta object.
 */
export const getArtaSubtype = (categorySlug: string, type?: string): ArtaObject['subtype'] => {
  if (categorySlug.startsWith('painting')) {
    return type === 'framed' ? 'painting_framed' : 'painting_unframed';
  }
  if (
    categorySlug.startsWith('print') ||
    categorySlug.startsWith('drawing') ||
    categorySlug.startsWith('collage')
  ) {
    return type === 'framed' ? 'work_on_paper_framed' : 'work_on_paper_unframed';
  }
  if (categorySlug.startsWith('photography')) {
    return type === 'framed' ? 'photograph_framed' : 'photograph_unframed';
  }
  if (categorySlug.startsWith('sculpture')) {
    return 'sculpture';
  }

  return 'other';
};

export const isArtaQuoteType = (quoteType: string): quoteType is ArtaQuoteType =>
  quoteType === 'parcel' ||
  quoteType === 'premium' ||
  quoteType === 'select' ||
  quoteType === 'self_ship';

export const getEstimatesTotal = (estimates: EstimatesMap): number | null =>
  Object.values(estimates).reduce((total, estimate) => {
    if (estimate.status === 'quoted') {
      total += estimate?.shipping_price?.min || 0;
    }
    return total;
  }, 0) || null;

export const isArtaSupportedCurrency = (currency: string): currency is ArtaSupportedCurrency => {
  const mapping: Record<string, ArtaSupportedCurrency | null> = {
    GBP: 'GBP',
    USD: 'USD',
    EUR: 'EUR',
    CAD: 'CAD',
    AUD: null,
  };
  return !!mapping[currency];
};

export const filterEstimatesByCurrency = ({
  estimatesMap,
  currency,
}: {
  estimatesMap: EstimatesMap;
  currency: ArtaSupportedCurrency;
}): EstimatesMap =>
  Object.fromEntries(
    Object.entries(estimatesMap).filter(([, estimate]) => estimate.currency === currency),
  );

/**
 * @description Generates a unique cache key for an artwork provider based on its details.
 * Key comes in format `ArtworkProvider:{providerSlug}_Objects:{objectsCount}_Currency:{currency}_Products:{productSlugs}`
 * @param {object} params
 * @param {string} params.providerSlug a unique identifier for artist or provider, ID or slug
 * @param {ArtaObject[]} params.objects list of Arta objects to be shipped
 * @param {"GBP" | "USD" | "EUR" | "CAD"} params.currency arta supported currency
 * @param {string[]} params.productSlugs list of product slugs for the given provider
 * @returns {string} a unique generated string value: compound of ID, objects count and currency
 */
export const getProviderCacheKey = ({
  providerSlug,
  objects,
  currency,
  productSlugs,
}: {
  providerSlug: string | number;
  objects: ArtaObject[];
  currency: ArtaSupportedCurrency;
  productSlugs: string[];
}) => {
  return `ArtworkProvider:${providerSlug}_Objects:${objects.length}_Currency:${currency}_Products:${productSlugs.join('|')}`;
};

/**
 * @description Decodes a provider cache key string into its components.
 * @param {string} cacheKey - The cache key string to decode.
 * Key comes in format `ArtworkProvider:{providerSlug}_Objects:{objectsCount}_Currency:{currency}_Products:{productSlugs}`
 * @returns {object} - The decoded components of the provider cache key.
 */
export const decodeProviderCacheKey = (cacheKey: string) => {
  const keyRegexp = /ArtworkProvider:(.+)_Objects:(\d+)_Currency:(\w+)_Products:(.+)/;
  const match = cacheKey.match(keyRegexp);
  if (!match) {
    throw new Error(`Invalid cacheKey format: ${cacheKey}`);
  }
  const providerSlug = match[1];
  const objectsCount = parseInt(match[2], 10);
  const currency = match[3] as ArtaSupportedCurrency;
  const productSlugs = match[4].split('|');

  return {providerSlug, objectsCount, currency, productSlugs};
};

/**
 * Fetches the Arta sessions for the given providers and currency.
 * @param {object} params
 * @param {ArtworkProvider[]} params.providers - The list of artwork providers.
 * @param {ArtaSupportedCurrency} params.currency - The currency to use for the request.
 * @returns {Promise<{data: SessionsMap; error: null} | {data: null; error: string}>}
 */
export const fetchArtaSessionsMap = async ({
  providers,
  currency,
}: {
  providers: ArtworkProvider[];
  currency: ArtaSupportedCurrency;
}): Promise<{data: SessionsMap; error: null} | {data: null; error: string}> => {
  try {
    if (!process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY) {
      throw new Error('Arta API Publishable Key is not defined');
    }
    const sessions = await Promise.all(
      providers.map(async (provider) => {
        const {data} = await axios<ArtaHostedSession>({
          method: 'POST',
          url: `${ARTA_BASE_URL}/estimate/hosted_sessions`,
          headers: {
            authorization: `${ARTA_PREFIX} ${process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY}`,
            'cache-control': 'no-cache',
            'content-type': 'application/json',
          },
          data: {
            hosted_session: {
              origin: provider.origin,
              objects: provider.objects,
              currency,
            },
          },
        });
        const session = getHostedSessionParams(data);
        return {
          cacheKey: provider.cacheKey,
          session,
        };
      }),
    );

    const sessionsMap = sessions.reduce((map, current) => {
      map[current.cacheKey] = {...current.session, timestamp: unixTimestamp.get()};
      return map;
    }, {} as SessionsMap);

    return {
      data: sessionsMap,
      error: null,
    };
  } catch (err) {
    return {
      data: null,
      error: getArtaErrorMessage({error: err, defaultMessage: 'Failed to fetch Arta session'}),
    };
  }
};

interface ProviderWithSessions extends ArtworkProvider {
  session: {
    id: string | number;
    token: string;
    timestamp: number;
  };
}

export const getProvidersWithSessions = ({
  providers,
  sessionsMap,
}: {
  providers: ArtworkProvider[];
  sessionsMap: SessionsMap;
}): ProviderWithSessions[] => {
  return providers.reduce((list: ProviderWithSessions[], provider) => {
    const session = sessionsMap[provider.cacheKey];

    if (!!session) {
      list.push({
        ...provider,
        session,
      });
    }

    return list;
  }, [] as ProviderWithSessions[]);
};

interface SettledEstimateResults {
  success: EstimatesMap;
  errors: string[];
}

export const formatPromiseSettledResults = (
  promiseResults: PromiseSettledResult<{
    cacheKey: string;
    data: ArtaQuoteRequest;
    productSlugs: string[];
    quoteTypes: ArtaQuoteType[];
  }>[],
): SettledEstimateResults => {
  const results = promiseResults.reduce(
    (final: SettledEstimateResults, currentResult) => {
      if (currentResult.status === 'fulfilled') {
        final.success[currentResult.value.cacheKey] = getShippingEstimate(
          currentResult.value.data,
          currentResult.value.productSlugs.join('|'),
          currentResult.value.quoteTypes,
        );
      } else {
        const errorMessage = getArtaErrorMessage({
          error: currentResult.reason,
          defaultMessage: 'Failed to get estimate',
        });
        final.errors.push(errorMessage);
      }

      return final;
    },
    {
      success: {},
      errors: [],
    } as SettledEstimateResults,
  );

  return {
    ...results,
    errors: [...new Set(results.errors)],
  };
};

/**
 * @description Fetches shipping estimates from Arta for the given providers, currency, destination, and sessions.
 * @param {object} params
 * @param {ArtworkProvider[]} params.providers - The list of artwork providers.
 * @param {ArtaSupportedCurrency} params.currency - The currency to use for the request.
 * @param {ArtaLocation} params.destination - The destination location for the shipping estimate.
 * @param {SessionsMap} params.sessionsMap - The map of active sessions for the providers.
 * @returns {Promise<{data: EstimatesMap; error: null} | {data: null; error: string}>}
 */
export const fetchArtaEstimatesMap = async ({
  providers,
  currency,
  destination,
  sessionsMap,
}: {
  providers: ArtworkProvider[];
  currency: ArtaSupportedCurrency;
  destination: ArtaLocation;
  sessionsMap: SessionsMap;
}): Promise<
  {data: EstimatesMap; error: null; info?: string} | {data: null; error: string; info?: string}
> => {
  try {
    if (!process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY) {
      throw new Error('Arta API Publishable Key is not defined');
    }

    const providersWithSessions = getProvidersWithSessions({
      providers,
      sessionsMap,
    });

    const settledResults = await Promise.allSettled(
      providersWithSessions.map(async (currentProvider) => {
        const {session, objects, origin, quoteTypes, cacheKey, productSlugs} = currentProvider;

        const {data} = await axios<ArtaQuoteRequest>({
          method: 'POST',
          url: `${ARTA_BASE_URL}/estimate/requests`,
          headers: {
            authorization: `${ARTA_PREFIX} ${process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY}`,
            'hosted-session-id': session.id.toString(),
            'hosted-session-private-token': session.token,
            'cache-control': 'no-cache',
            'content-type': 'application/json',
          },
          data: {
            request: {
              origin,
              objects,
              currency,
              destination,
              preferred_quote_types: quoteTypes,
              insurance: ARTA_INSURANCE,
            },
          },
        });

        /**
         * @description handle cases where no quotes are returned or status is
         *  not 'quoted' and not 'disqualified'
         * - other statuses: no quotes available
         */
        if (data.status !== 'quoted' && data.status !== 'disqualified') {
          throw new Error(`ArtworkProvider: ${cacheKey} | Status: ${data.status}`);
        }

        return {
          cacheKey,
          data,
          productSlugs,
          quoteTypes,
        };
      }),
    );
    const finalResults = formatPromiseSettledResults(settledResults);

    if (isObjectEmpty(finalResults.success)) {
      /** No successful results case */
      return {
        data: null,
        error: finalResults.errors.join(', '),
      };
    }

    return {
      data: finalResults.success,
      error: null,
      /** Info message if any of the requests have failed while the rest succeeded */
      info:
        finalResults.errors.length > 0 ? [...new Set(finalResults.errors)].join(', ') : undefined,
    };
  } catch (err) {
    return {
      data: null,
      error: getArtaErrorMessage({error: err, defaultMessage: 'Failed to fetch Arta estimate'}),
    };
  }
};

/**
 * @description verifies the sessions cache for the given providers
 * - returns valid cached sessions and missing providers that need new sessions
 * @param {object} params
 * @param {ArtworkProvider[]} params.providers - The list of artwork providers.
 * @param {SessionsMap} params.sessionsCache - The map of cached sessions.
 * @returns {object} - An object containing valid cached sessions and missing providers.
 */
export const getProvidersBySessionsCache = ({
  providers,
  sessionsCache,
}: {
  providers: ArtworkProvider[];
  sessionsCache: SessionsMap;
}) => {
  const validCachedSessions: SessionsMap = {};
  const missingProviders: ArtworkProvider[] = [];

  providers.forEach((provider) => {
    const cached = sessionsCache[provider.cacheKey];
    if (
      !!cached &&
      !unixTimestamp.isExpired(cached.timestamp, ONE_DAY_MS) // sessions are valid for 24 hours
    ) {
      validCachedSessions[provider.cacheKey] = cached;
    } else {
      missingProviders.push(provider);
    }
  });

  return {validCachedSessions, missingProviders};
};

/**
 * @description verifies the estimates cache for the given providers and destination
 * - returns valid cached estimates and missing providers that need new estimates
 * @param {object} params
 * @param {ArtworkProvider[]} params.providers - The list of artwork providers.
 * @param {EstimatesMap} params.estimatesCache - The map of cached estimates.
 * @param {ArtaLocation} params.destination - The destination location for the shipping estimate.
 * @returns {object} - An object containing valid cached estimates and missing providers.
 */
export const getProvidersByEstimatesCache = ({
  providers,
  estimatesCache,
  destination,
}: {
  providers: ArtworkProvider[];
  estimatesCache: EstimatesMap;
  destination: ArtaLocation;
}) => {
  const validCachedEstimates: EstimatesMap = {};
  const missingProviders: ArtworkProvider[] = [];

  providers.forEach((provider) => {
    const cached = estimatesCache[provider.cacheKey];
    if (
      !!cached &&
      !unixTimestamp.isExpired(cached.timestamp) &&
      cached.to.country === destination.country &&
      cached.to.city === destination.city &&
      /** only use cached estimates with 'quoted' status
       * the 'disqualified' estimates are to be re-fetched
       */
      cached.status === 'quoted'
    ) {
      validCachedEstimates[provider.cacheKey] = cached;
    } else {
      missingProviders.push(provider);
    }
  });

  return {validCachedEstimates, missingProviders};
};

/**=========== PDP page managed shipping utils =========*/

/**
 * @description Props getter util for Managed Shipping component on PDP page
 * @returns {object|null} - The managed shipping props including providers, currency;
 * `null` if not available or input invalid.
 */
export const getManagedShippingPropsFromProductPage = ({
  productSlug,
  productType,
  providerSlug,
  selectedCurrency,
  categorySlug,
  managedShipping,
}: {
  providerSlug: string;
  productSlug: string;
  selectedCurrency: CurrencyType;
  categorySlug: string;
  productType: 'original' | 'prints';
  managedShipping: ProductData['managedShipping'];
}): {
  providers: ArtworkProvider[];
  currency: ArtaSupportedCurrency;
} | null => {
  const managedShippingData =
    productType === 'original' ? managedShipping.original : managedShipping.prints[productSlug];

  if (isObjectEmpty(managedShippingData)) {
    return null;
  }

  const {
    origin,
    quote_type,
    shipping_width,
    shipping_height,
    shipping_depth,
    value,
    value_currency,
  } = managedShippingData;

  if (!isArtaSupportedCurrency(selectedCurrency)) {
    return null;
  }
  if (!isArtaSupportedCurrency(value_currency)) {
    return null;
  }
  if (!isArtaQuoteType(quote_type)) {
    return null;
  }

  const artaObject: ArtaObject = {
    width: shipping_width,
    height: shipping_height,
    depth: shipping_depth,
    unit_of_measurement: 'cm',
    subtype: getArtaSubtype(categorySlug),
    value: value,
    value_currency: value_currency,
  };
  const cacheKey = getProviderCacheKey({
    providerSlug,
    objects: [artaObject],
    currency: selectedCurrency,
    productSlugs: [productSlug],
  });

  const provider: ArtworkProvider = {
    cacheKey,
    productSlugs: [productSlug],
    origin,
    objects: [artaObject],
    quoteTypes: [quote_type],
  };

  return {
    providers: [provider],
    currency: selectedCurrency as ArtaSupportedCurrency,
  };
};

/**=========== BASKET page managed shipping utils =========*/

type BasketItemShipping = Pick<
  ManagedShippingDataProps,
  'quote_type' | 'packing' | 'shipping_width' | 'shipping_height' | 'shipping_depth'
>;

/**
 * @description Constructs an ArtworkProvider object from a basketed group.
 * @param {object} group - The basketed group to construct the provider from.
 * @param {"GBP" | "USD" | "EUR" | "CAD"} currency - The arta currency to use for the provider.
 * @returns The constructed ArtworkProvider object.
 */
export const getArtworkProviderFromGroup = (
  group: BasketProps['basketed_groups'][number],
  currency: ArtaSupportedCurrency,
): ArtworkProvider => {
  const {provider_slug, basketed_items, origin} = group;

  /** verify basketed items have shipping non nullable */
  const basketedItemsWithShipping = (
    basketed_items as BasketedItemProps<'artwork', Record<string, string>>[]
  ).filter((item) => !!item.shipping);

  const objects: ArtaObject[] = basketedItemsWithShipping.map((item) => {
    const categorySlug = item.item.category_slug;
    const itemManagedShipping = item.shipping as BasketItemShipping;

    return {
      width: itemManagedShipping.shipping_width,
      height: itemManagedShipping.shipping_height,
      depth: itemManagedShipping.shipping_depth,
      unit_of_measurement: 'cm',
      subtype: getArtaSubtype(categorySlug),
      value: formatToFloat(item.pricing[currency].price) || 0,
      value_currency: currency,
    };
  });
  const productSlugs: string[] = basketed_items.map((item) => item.slug);
  const objectsQuoteTypes = basketed_items
    .map((item) => (item.shipping as BasketItemShipping).quote_type)
    .filter((type) => isArtaQuoteType(type));

  return {
    cacheKey: getProviderCacheKey({providerSlug: provider_slug, objects, currency, productSlugs}),
    productSlugs,
    origin,
    objects,
    quoteTypes: [...new Set(objectsQuoteTypes)],
  };
};

const getSupportedGroups = (groups: BasketedGroupProps[]) => {
  const managedShipping = groups.filter((group) => group.is_managed_shipping);
  const productShipping = groups.filter((group) => !group.is_managed_shipping);

  return {managedShipping, productShipping};
};

/**
 * @description Calculates the total shipping cost for a list of basketed groups
 *  that have product shipping information.
 * @param {BasketedGroupProps[]} groups - The list of basketed groups.
 * @returns {number} - The total shipping cost.
 */
export const getGroupShippingTotal = (groups: BasketedGroupProps[]) => {
  const totalCost = groups.reduce((total, current) => {
    const cost = current.shipping?.cost ? parseFloat(current.shipping?.cost) : 0;
    total += cost;
    return total;
  }, 0);
  return totalCost;
};

/**
 * @description Formats estimates map to be keyed by provider slug for Basket page
 * @param {object} estimates - The estimates map keyed by provider cache key.
 * @returns {object} - The formatted estimates map keyed by provider slug.
 */
const formatMapToBasketPage = (estimates: EstimatesMap): EstimatesMap => {
  const estimatesEntriesForBasket = Object.entries(estimates).map(([cacheKey, estimate]) => {
    const {providerSlug} = decodeProviderCacheKey(cacheKey);

    return [providerSlug, estimate] as [string, ArtaShippingEstimate];
  });

  return Object.fromEntries(estimatesEntriesForBasket);
};

export const formatEstimateStateToBasketPage = (
  state: AsyncState<EstimatesMap>,
): AsyncState<EstimatesMap> => {
  let updatedState = state;
  if (state.data) {
    updatedState = {
      ...state,
      data: formatMapToBasketPage(state.data),
    };
  }
  return updatedState;
};

export const getBasketEstimatesContent = ({
  estimatesMap,
  productShipping,
  isCurrencySupported,
}: {
  estimatesMap: EstimatesMap | null;
  productShipping: BasketedGroupProps[];
  isCurrencySupported: boolean;
}) => {
  /** get managed shipping total from estimates map */
  let managedShippingEstimatesTotal: number | null = null;

  if (isCurrencySupported) {
    managedShippingEstimatesTotal = estimatesMap ? getEstimatesTotal(estimatesMap) : null;
  }

  const productShippingTotal = productShipping.length
    ? productShipping.reduce(
        (total, provider) => total + parseFloat(provider.shipping?.cost || '0'),
        0,
      )
    : null;

  const valueShippingTotal = managedShippingEstimatesTotal
    ? managedShippingEstimatesTotal + (productShippingTotal || 0)
    : productShippingTotal;

  return {
    total: valueShippingTotal
      ? {
          value: valueShippingTotal,
          price: formatNumericToPrice(valueShippingTotal),
        }
      : null,
    managedShipping: {
      total: managedShippingEstimatesTotal
        ? {
            value: managedShippingEstimatesTotal,
            price: formatNumericToPrice(managedShippingEstimatesTotal),
          }
        : null,
    },
    providersNotSupported: {
      total: productShippingTotal
        ? {
            value: productShippingTotal,
            price: formatNumericToPrice(productShippingTotal),
          }
        : null,
    },
  };
};

export const validateBasket = (basket: BasketProps) => {
  let isValid = true;
  if (!basket.basketed_groups.length) {
    isValid = false;
  }
  const hasNonShippableGroup = basket.basketed_groups.some(
    (group) => group.is_shippable === false || group.basketed_items.length === 0,
  );
  if (hasNonShippableGroup) {
    isValid = false;
  }

  const hasAnySupportedProvider = basket.basketed_groups.some((group) => group.is_managed_shipping);
  if (!hasAnySupportedProvider) {
    isValid = false;
  }

  return isValid;
};

/**
 * @description Props getter util for Managed Shipping component on Basket page
 * @param {object} params
 * @param {BasketProps} params.basket - The basket object containing basketed groups and order summary.
 * @returns {object} - An object containing the managed shipping properties:
 * - providers: array of ArtworkProvider objects for the managed shipping groups.
 * - currency: the Arta supported currency for the basket.
 * - productShipping: array of BasketedGroupProps that are not supported for managed shipping.
 */
export const getManagedShippingPropsFromBasket = ({
  basket,
  estimatesMap,
}: {
  basket: BasketProps;
  estimatesMap: EstimatesMap | null;
}): {
  providers: ArtworkProvider[];
  currency: ArtaSupportedCurrency;
  productShipping: BasketedGroupProps[];
  content: ReturnType<typeof getBasketEstimatesContent>;
  isCurrencySupported: boolean;
} | null => {
  const isBasketValid = validateBasket(basket);

  if (!isBasketValid) {
    return null;
  }

  const isCurrencySupported = isArtaSupportedCurrency(basket.order_summary.currency.code);

  const groups = basket.basketed_groups;
  const artaCurrency = basket.order_summary.currency.code as ArtaSupportedCurrency;
  const {managedShipping, productShipping} = getSupportedGroups(groups);
  const providers = managedShipping.map((group) =>
    getArtworkProviderFromGroup(group, artaCurrency),
  );

  const content = getBasketEstimatesContent({
    estimatesMap,
    productShipping,
    isCurrencySupported,
  });

  if (!isCurrencySupported) {
    return {
      providers,
      currency: basket.order_summary.currency.code as ArtaSupportedCurrency,
      productShipping,
      content,
      isCurrencySupported,
    };
  }

  return {
    providers,
    currency: artaCurrency,
    productShipping,
    content,
    isCurrencySupported,
  };
};

/**
 * @description Gets the shipping details for a basketed group based on the provided estimates.
 * @param params - The parameters object.
 * @param params.providerSlug - The slug of the artwork provider.
 * @param params.estimates - The asynchronous state containing shipping estimates.
 * @param params.group - The basketed group for which to get the shipping details.
 * @param params.isCurrencySupported - Flag indicating if the currency is supported.
 * @return {ShippingProps | undefined} - The shipping details or `undefined` if not available.
 */
export const getBasketGroupManagedShippingInfo = ({
  providerSlug,
  estimates,
  group,
  isCurrencySupported,
}: {
  providerSlug: string;
  estimates: AsyncState<EstimatesMap>;
  group: BasketedGroupProps;
  isCurrencySupported: boolean;
}): ShippingProps => {
  /** ⚠️ TODO: remove stub value of 5 when basket API MS is ready */
  const dispatchDays = group.basketed_items[0].shipping?.dispatch_days || 5;

  const estimate = estimates.data?.[providerSlug];

  if (estimates.status === 'error' || estimate?.status === 'disqualified') {
    return {
      cost: MANAGED_SHIPPING_ESTIMATE_FAILED,
      dispatch_days: dispatchDays,
      country_of_origin: group.origin.country,
    };
  }

  let cost =
    typeof estimate?.shipping_price?.min === 'number'
      ? formatNumericToPrice(estimate.shipping_price.min)
      : MANAGED_SHIPPING_COST_UNAVAILABLE;

  if (!isCurrencySupported) {
    cost = MANAGED_SHIPPING_COST_UNAVAILABLE;
  }

  return {
    cost,
    dispatch_days: dispatchDays,
    country_of_origin: group.origin.country,
  };
};
